import { AiClient, AiClientResult, AiOptions, AiTool, AiStreamSink } from "@me8eon/aiclient";
import { isAssistantMessageRequest, Message, ToolCall, ToolMessage } from "@me8eon/messages";
import { ErrorsOr, LogAnd } from "@me8eon/errors";
import { LLM_MODEL } from "@me8eon/config";
import {
    ChatCompletion,
    ChatCompletionAssistantMessageParam,
    ChatCompletionCreateParamsNonStreaming,
    ChatCompletionCreateParamsStreaming,
    ChatCompletionContentPart,
    ChatCompletionContentPartText,
    ChatCompletionFunctionTool,
    ChatCompletionMessageParam,
    ChatCompletionMessageToolCall,
    ChatCompletionSystemMessageParam,
    ChatCompletionTool,
    ChatCompletionUserMessageParam
} from "openai/resources/chat";
import { AxiosResponse, AxiosStatic } from "axios";

type InternalTextMessage = {
    type: "text";
    text: string;
};

type InternalImageMessage = {
    type: "image_url";
    image_url: {
        url: string;
    };
};

type InternalContent = string | Array<InternalTextMessage | InternalImageMessage>;

type OpenAiAiOptions = AiOptions & {
    stream?: {
        sink?: AiStreamSink;
    };
};

function isStringContent(content: InternalContent): content is string {
    return typeof content === "string";
}

function toTextOnlyContent(content: InternalContent): string | ChatCompletionContentPartText[] {
    if (typeof content === "string") return content;

    const textParts = content
        .filter((part): part is InternalTextMessage => part.type === "text")
        .map<ChatCompletionContentPartText>(part => ({
            type: "text",
            text: part.text,
        }));

    if (textParts.length !== content.length) {
        throw new Error("This message role only supports text content");
    }

    return textParts;
}

function toUserContent(content: InternalContent): string | ChatCompletionContentPart[] {
    if (typeof content === "string") return content;

    return content.map<ChatCompletionContentPart>(part => {
        if (part.type === "text") {
            return {
                type: "text",
                text: part.text,
            };
        }

        return {
            type: "image_url",
            image_url: {
                url: part.image_url.url,
            },
        } as ChatCompletionContentPart;
    });
}

function toToolContent(content: InternalContent): string {
    if (typeof content === "string") return content;
    throw new Error("Tool messages must have string content");
}

const normalizeAiClientMessageForOpenAi = (messages: Message[]): Array<ChatCompletionMessageParam> => {
    return messages.map(message => {
        if (isAssistantMessageRequest(message)) {
            const newMessage: ChatCompletionAssistantMessageParam = {
                role: "assistant",
                content: null,
                tool_calls: message.toolCalls.map(tc => ({
                    id: tc.tool_call_id,
                    type: "function",
                    function: {
                        name: tc.tool_name,
                        arguments: JSON.stringify(tc.tool_args),
                    },
                })),
            };
            return newMessage;
        }

        if (message.role === "system") {
            const newMessage: ChatCompletionSystemMessageParam = {
                role: "system",
                content: toTextOnlyContent(message.content as InternalContent),
            };
            return newMessage;
        }

        if (message.role === "user") {
            const newMessage: ChatCompletionUserMessageParam = {
                role: "user",
                content: toUserContent(message.content as InternalContent),
            };
            return newMessage;
        }

        if (message.role === "assistant") {
            const newMessage: ChatCompletionAssistantMessageParam = {
                role: "assistant",
                content: isStringContent(message.content as InternalContent)
                    ? (message.content as string)
                    : toTextOnlyContent(message.content as InternalContent),
            };
            return newMessage;
        }

        if (message.role === "tool") {
            const toolMessage = message as ToolMessage;

            try {
                const parsed = JSON.parse(toToolContent(toolMessage.content as InternalContent));
                return {
                    role: "tool" as const,
                    content: String(parsed?.value ?? ""),
                    tool_call_id: toolMessage.tool_call_id,
                };
            } catch {
                return {
                    role: "tool" as const,
                    content: toToolContent(toolMessage.content as InternalContent),
                    tool_call_id: toolMessage.tool_call_id,
                };
            }
        }

        throw new Error(`Unsupported message role or type: ${(message as any).role}`);
    });
};

const normaliseOpenAiToolsForAiClient = (tools: Array<ChatCompletionMessageToolCall>): ToolCall[] => {
    if (!tools) return [];
    return tools.map(tool => {
        if (tool.type === "function") {
            return {
                tool_call_id: tool.id,
                tool_name: tool.function.name,
                tool_args: JSON.parse(tool.function.arguments),
            };
        }
        throw new Error(`Unsupported tool call type: ${tool.type}`);
    });
};

function isGeminiModel(model?: string): boolean {
    return !!model && model.toLowerCase().includes("gemini");
}

function normaliseToolParametersForProvider(parameters: any, gemini: boolean): Record<string, any> {
    const isObjectSchema =
        !!parameters &&
        parameters.type === "object" &&
        typeof parameters.properties === "object" &&
        parameters.properties !== null;

    const safeObjectSchema = isObjectSchema
        ? {
            type: "object",
            properties: parameters.properties || {},
            required: Array.isArray(parameters.required) ? parameters.required : [],
            ...(gemini
                ? {}
                : {
                    additionalProperties:
                        typeof parameters.additionalProperties === "boolean"
                            ? parameters.additionalProperties
                            : false,
                }),
        }
        : {
            type: "object",
            properties: {},
            required: [],
            ...(gemini ? {} : { additionalProperties: false }),
        };

    if (gemini && Object.keys(safeObjectSchema.properties || {}).length === 0) {
        // Vertex/Gemini function-calling rejects some empty-object schemas.
        // Keep an optional no-op field to preserve "no required args" semantics.
        safeObjectSchema.properties = {
            _noop: {
                type: "string",
                description: "Optional placeholder argument. Ignore this field.",
            },
        };
    }

    return safeObjectSchema;
}

const normaliseAiClientToolsForOpenAi = (tools: AiTool[], model?: string): Array<ChatCompletionTool> => {
    const gemini = isGeminiModel(model);
    return tools.map(aiClientTool => {
        const safeParameters = normaliseToolParametersForProvider(aiClientTool.parameters, gemini);
        const openaiTool: ChatCompletionFunctionTool = {
            type: "function",
            function: {
                name: aiClientTool.name,
                description: aiClientTool.description,
                parameters: safeParameters,
                ...(gemini ? {} : { strict: true }),
            },
        };
        return openaiTool;
    });
};

type OpenAiClientConfig = {
    axios: AxiosStatic;
    apiKey: () => Promise<string | undefined>;
    baseURL: string;
    defaultModel?: string;
};

type StreamToolCallAccumulator = {
    id: string;
    type: "function";
    function: {
        name: string;
        arguments: string;
    };
};

type StreamAccumulator = {
    content: string;
    toolCalls: Record<number, StreamToolCallAccumulator>;
};

function buildNonStreamingRequestBody(
    messages: Message[],
    options: OpenAiAiOptions,
    defaultModel?: string
): ChatCompletionCreateParamsNonStreaming {
    const model = options.model || defaultModel || LLM_MODEL;
    return {
        model,
        messages: normalizeAiClientMessageForOpenAi(messages),
        tools: options.tools ? normaliseAiClientToolsForOpenAi(options.tools, model) : undefined,
        temperature: options.temperature !== undefined ? options.temperature : 0.7,
        response_format: options.response_format ? options.response_format : undefined,
    };
}

function buildStreamingRequestBody(
    messages: Message[],
    options: OpenAiAiOptions,
    defaultModel?: string
): ChatCompletionCreateParamsStreaming {
    const model = options.model || defaultModel || LLM_MODEL;
    return {
        model,
        messages: normalizeAiClientMessageForOpenAi(messages),
        tools: options.tools ? normaliseAiClientToolsForOpenAi(options.tools, model) : undefined,
        temperature: options.temperature !== undefined ? options.temperature : 0.7,
        response_format: options.response_format ? options.response_format : undefined,
        stream: true,
    };
}

function buildFinalMessage(acc: StreamAccumulator): AiClientResult {
    const orderedToolCalls = Object.entries(acc.toolCalls)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, tc]) => ({
            id: tc.id,
            type: "function" as const,
            function: {
                name: tc.function.name,
                arguments: tc.function.arguments,
            },
        }));

    const toolCalls = normaliseOpenAiToolsForAiClient(orderedToolCalls as Array<ChatCompletionMessageToolCall>);
    const message: Message =
        toolCalls.length > 0
            ? { role: "assistant", content: "", toolCalls }
            : { role: "assistant", content: acc.content };

    return {
        message,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
    };
}

async function handleSsePayload(payload: any, acc: StreamAccumulator, sink?: AiStreamSink): Promise<void> {
    const choice = payload?.choices?.[0];
    if (!choice) return;

    const delta = choice.delta || {};

    if (typeof delta.content === "string" && delta.content.length > 0) {
        acc.content += delta.content;
        await sink?.({
            type: "text-delta",
            delta: delta.content,
            content: acc.content,
        });
    }

    if (Array.isArray(delta.tool_calls)) {
        for (const toolDelta of delta.tool_calls) {
            const index = toolDelta.index ?? 0;
            const existing = acc.toolCalls[index] || {
                id: "",
                type: "function" as const,
                function: {
                    name: "",
                    arguments: "",
                },
            };

            if (toolDelta.id) existing.id = toolDelta.id;
            if (toolDelta.function?.name) existing.function.name = toolDelta.function.name;
            if (toolDelta.function?.arguments) existing.function.arguments += toolDelta.function.arguments;

            acc.toolCalls[index] = existing;
        }
    }
}

async function streamingCompletion(
    baseURL: string,
    headers: Record<string, string>,
    messages: Message[],
    options: OpenAiAiOptions,
    defaultModel?: string
): Promise<LogAnd<ErrorsOr<AiClientResult>>> {
    const body = buildStreamingRequestBody(messages, options, defaultModel);
    const sink = options.stream?.sink;

    try {
        const response = await fetch(`${baseURL}/service/completions`, {
            method: "POST",
            headers: {
                ...headers,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            let errMsg = text || `HTTP ${response.status}`;
            try {
                const parsed = JSON.parse(text);
                errMsg = parsed?.error?.message || errMsg;
            } catch {
            }

            await sink?.({
                type: "error",
                error: errMsg,
            });

            return {
                errors: [errMsg],
                log: { whatHappened: "openai.call.failed", severity: "error", params: { status: response.status, text } },
            };
        }

        if (!response.body) {
            const errMsg = "Response body is empty";
            await sink?.({
                type: "error",
                error: errMsg,
            });

            return {
                errors: [errMsg],
                log: { whatHappened: "openai.call.failed", severity: "error", params: { reason: "empty-body" } },
            };
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        const acc: StreamAccumulator = {
            content: "",
            toolCalls: {},
        };

        let textStarted = false;
        let buffer = "";

        const startTextIfNeeded = async () => {
            if (!textStarted) {
                textStarted = true;
                await sink?.({ type: "text-start" });
            }
        };

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const parts = buffer.split("\n\n");
            buffer = parts.pop() || "";

            for (const part of parts) {
                const lines = part
                    .split("\n")
                    .map(line => line.trim())
                    .filter(Boolean);

                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;

                    const raw = line.slice(5).trim();

                    if (raw === "[DONE]") {
                        continue;
                    }

                    const payload = JSON.parse(raw);
                    const delta = payload?.choices?.[0]?.delta;

                    const hasTextDelta =
                        typeof delta?.content === "string" &&
                        delta.content.length > 0;

                    if (hasTextDelta) {
                        await startTextIfNeeded();
                    }

                    await handleSsePayload(payload, acc, sink);
                }
            }
        }

        const finalTail = buffer.trim();
        if (finalTail.startsWith("data:")) {
            const raw = finalTail.slice(5).trim();
            if (raw && raw !== "[DONE]") {
                const payload = JSON.parse(raw);
                const delta = payload?.choices?.[0]?.delta;
                const hasTextDelta =
                    typeof delta?.content === "string" &&
                    delta.content.length > 0;

                if (hasTextDelta) {
                    await startTextIfNeeded();
                }

                await handleSsePayload(payload, acc, sink);
            }
        }

        if (textStarted) {
            await sink?.({
                type: "text-end",
                content: acc.content,
            });
        }

        return {
            value: buildFinalMessage(acc),
            log: "openai.call.success",
        };
    } catch (error) {
        console.error(error);
        const errMsg = (error as any)?.message || "Unknown error";

        await sink?.({
            type: "error",
            error: errMsg,
        });

        return {
            errors: [errMsg],
            log: { whatHappened: "openai.call.failed", severity: "error", params: { error: String(error) } },
        };
    }
}

async function nonStreamingCompletion(
    axiosInstance: ReturnType<AxiosStatic["create"]>,
    headers: Record<string, string>,
    messages: Message[],
    options: OpenAiAiOptions,
    defaultModel?: string
): Promise<LogAnd<ErrorsOr<AiClientResult>>> {
    try {
        const body = buildNonStreamingRequestBody(messages, options, defaultModel);

        const response: AxiosResponse<ChatCompletion> = await axiosInstance.post(
            `/service/completions`,
            body,
            { headers }
        );

        const choice = response.data.choices[0];
        const lastMessage = choice.message;
        const toolCalls = normaliseOpenAiToolsForAiClient(lastMessage.tool_calls || []);
        const msg: Message = {
            role: lastMessage.role,
            content: lastMessage.content || "",
        };

        if (lastMessage.role === "assistant" && toolCalls.length > 0) {
            (msg as any).toolCalls = toolCalls;
        }

        return { value: { message: msg, toolCalls }, log: "openai.call.success" };
    } catch (error) {
        console.error(error);
        const errMsg = (error as any)?.response?.data?.error?.message || (error as any)?.message || "Unknown error";

        await options.stream?.sink?.({
            type: "error",
            error: errMsg,
        });

        return {
            errors: [errMsg],
            log: { whatHappened: "openai.call.failed", severity: "error", params: { error: String(error) } },
        };
    }
}

export const createOpenAiClient = (config: OpenAiClientConfig): AiClient => {
    const { axios, baseURL, apiKey, defaultModel } = config;
    const axiosInstance = axios.create({ baseURL });

    return async (messages: Message[], options: AiOptions = {}): Promise<LogAnd<ErrorsOr<AiClientResult>>> => {
        const openAiOptions = options as OpenAiAiOptions;
        const effectiveModel = openAiOptions.model || defaultModel;
        const useGeminiCompatNonStreaming = isGeminiModel(effectiveModel) && !!openAiOptions.tools?.length;

        // baseURL now points at backend-node-v2's own /service/completions proxy, which injects
        // the AI gateway credential server-side — only our own app JWT travels with the request.
        const headers: Record<string, string> = {
            Authorization: `Bearer ${await apiKey()}`,
        };

        if (openAiOptions.stream?.sink && !useGeminiCompatNonStreaming) {
            return streamingCompletion(baseURL, headers, messages, openAiOptions, defaultModel);
        }

        return nonStreamingCompletion(axiosInstance, headers, messages, openAiOptions, defaultModel);
    };
};